import { useCallback, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { useFirmNotificationPoliciesQuery } from '@/lib/api/firm-notifications';
import {
  useMyPreferencesQuery,
  usePutGlobalPreferencesMutation,
  usePutEngagementPreferencesMutation,
  type DigestCadence,
} from '@/lib/api/my-notifications';
import { ChannelOverrides, type EngagementSummary } from '@/components/settings/channel-overrides';

/**
 * My notifications — `/settings/my-notifications` (T-0112 / C20-122,
 * E09-F04 user scope).
 *
 * Per-user channel overrides + per-engagement mute toggles + email
 * digest cadence. Mirrors the prototype's `screens-gaps.js → firmMyNotifs()`.
 *
 * Wires T-0080:
 *   - GET  /api/v1/notifications/preferences         (read)
 *   - PUT  /api/v1/notifications/preferences/global  (channel overrides)
 *   - PUT  /api/v1/notifications/preferences/engagement/:engagement_id
 *           (per-engagement mutes)
 *
 * Caveats (see `my-notifications.ts` for full details):
 *   - Digest cadence has no matching backend field today; the selector
 *     is wired through component-local state with a backend gap flag.
 *   - The "my engagements" list does not have a dedicated endpoint
 *     either (see `engagements.ts` doc) — we derive the list from the
 *     per-engagement mutes returned by the prefs read. If the user has
 *     no mutes, the column renders an empty state.
 */
export const Route = createFileRoute('/settings/my-notifications')({
  component: MyNotificationsSettings,
});

function MyNotificationsSettings() {
  const policiesQuery = useFirmNotificationPoliciesQuery();
  const prefsQuery = useMyPreferencesQuery();
  const putGlobalMutation = usePutGlobalPreferencesMutation();
  const putEngagementMutation = usePutEngagementPreferencesMutation();

  // Local-only digest cadence selector — backend gap (see lib doc).
  const [digestCadence, setDigestCadence] = useState<DigestCadence>('daily');

  const myEngagements = useMemo<EngagementSummary[]>(() => {
    // Derive from per-engagement mute rows — the only signal we have of
    // which engagements the caller belongs to without a dedicated
    // /engagements?for_user=me endpoint. For v1 if there are no mute
    // rows, the column is empty (the user has only firm-level overrides).
    if (!prefsQuery.data) return [];
    const ids = new Set<string>();
    for (const m of prefsQuery.data.per_engagement_mutes) {
      ids.add(m.engagement_id);
    }
    // For the demo, show short labels — the future endpoint will return
    // client_name explicitly. Here we just use the engagement_id slug.
    return Array.from(ids).map((id) => ({
      engagement_id: id,
      client_name: id.split('-').slice(-1)[0] ?? id,
    }));
  }, [prefsQuery.data]);

  const handleSaveGlobal = useCallback(
    (subscriptions: Array<{ event_type: string; is_subscribed: boolean }>) => {
      putGlobalMutation.mutate({ subscriptions });
    },
    [putGlobalMutation],
  );

  const handleSaveEngagement = useCallback(
    (engagementId: string, mutes: Array<{ event_type: string; is_muted: boolean }>) => {
      putEngagementMutation.mutate({ engagementId, mutes });
    },
    [putEngagementMutation],
  );

  if (policiesQuery.isLoading || prefsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading preferences…
      </div>
    );
  }
  if (policiesQuery.error) {
    return <ErrorMessage error={policiesQuery.error} />;
  }
  if (prefsQuery.error) {
    return <ErrorMessage error={prefsQuery.error} />;
  }
  if (!policiesQuery.data || !prefsQuery.data) {
    return <p className="text-body-sm text-ink-500">No preferences available.</p>;
  }

  const saving = putGlobalMutation.isPending || putEngagementMutation.isPending;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="t-h1 font-semibold text-ink-900">My notifications</h1>
          <p className="t-meta text-ink-500">
            Personal overrides · {policiesQuery.data.policies.length} event types
          </p>
        </div>
      </header>

      {putGlobalMutation.error ? <ErrorMessage error={putGlobalMutation.error as Error} /> : null}
      {putEngagementMutation.error ? (
        <ErrorMessage error={putEngagementMutation.error as Error} />
      ) : null}

      <ChannelOverrides
        firmPolicies={policiesQuery.data.policies}
        myPrefs={prefsQuery.data}
        myEngagements={myEngagements}
        digestCadence={digestCadence}
        onDigestCadenceChange={setDigestCadence}
        onSaveGlobal={handleSaveGlobal}
        onSaveEngagement={handleSaveEngagement}
        saving={saving}
      />
    </div>
  );
}
